import { CaretDown, DirectionStraightRight } from "@carbon/icons-react";
import { Button, Step, StepConnector, Stepper } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useOrderContext } from "../../contexts/OrderContext";
import { useProgression } from "../../hooks/useProgression";
import "./ProgressBar.scss";
import ProgressBarPopup from "./progressBarPopup/ProgressBarPopup";

interface IProgressBarProps {
  disableNext: boolean;
  saveProgress: () => void;
}

const ProgressBar = (props: IProgressBarProps) => {
  const { t } = useTranslation();
  const { progressLevel, steps } = useOrderContext();
  const { goNext, goBack, jumpTo } = useProgression();

  const { disableNext, saveProgress } = props;

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const currentStep = steps.find((s) => s.number === progressLevel);

  const onProceed = () => {
    saveProgress();
    goNext();
  };

  const onBack = () => {
    saveProgress();
    goBack();
  };

  const onJumpTo = (setProgressLevelTo: number) => {
    saveProgress();
    setIsPopupOpen(false);
    jumpTo(setProgressLevelTo);
  };

  const renderStepElements = () => {
    return steps.map((step, index) => {
      return (
        <Step key={index}>
          <StepConnector />
        </Step>
      );
    });
  };

  const renderBackButton = () => (
    <div className="progressbar-step-choose-back">
      {/* no back button on the first screen  */}
      {progressLevel > 1 && (
        <Button data-testid="progressbar-step-choose-back" onClick={onBack}>
          <DirectionStraightRight size={"24"} className="mirror" />
        </Button>
      )}
    </div>
  );

  const renderCurrentStepButton = () => (
    <Button
      onClick={() => setIsPopupOpen(true)}
      variant="outlined"
      className="progressbar-step-button"
      data-testid="progressbar-step-button"
    >
      <div className="progressbar-step-button-icon">{currentStep?.icon}</div>
      <div className="progressbar-step-button-text">
        {t("step")} {progressLevel} / {steps.length}
        <div
          title={t(currentStep ? currentStep.text : "")}
          className="progressbar-step-button-name"
        >
          {currentStep && t(currentStep.text)}
        </div>
      </div>
      <div className="progressbar-step-button-icon">
        <CaretDown size={"24"} />
      </div>
    </Button>
  );

  const renderNextButton = () => (
    <div className="progressbar-step-choose-next">
      {/* no proceed button on the last page */}
      {progressLevel < steps.length && (
        <Button
          data-testid="progressbar-step-choose-next"
          onClick={onProceed}
          disabled={!currentStep?.done || disableNext}
        >
          <DirectionStraightRight size={"24"} />
        </Button>
      )}
    </div>
  );

  return (
    <div className="progressbar">
      <Stepper alternativeLabel activeStep={progressLevel}>
        {renderStepElements()}
      </Stepper>
      <div className="progressbar-step-choose">
        {renderBackButton()}
        {renderCurrentStepButton()}
        {renderNextButton()}
      </div>
      <ProgressBarPopup
        isOpen={isPopupOpen}
        handleClose={() => setIsPopupOpen(false)}
        progressLevel={progressLevel}
        onJumpTo={onJumpTo}
      />
    </div>
  );
};

export default ProgressBar;
